import { LitElement, html, css } from 'lit';

/**
 * The Estimate tool's destinations (Estimate Tool 2.2): Estimates from Phase E6, Deals from E17,
 * Data Hygiene from E2, Settings from E4. Each is a thin page around the card that does the
 * work, so the card owns its own request and the page owns only the layout.
 */

/** The Estimates list (Phase E6). */
class PageEstimates extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .stack {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 18px;
    }
  `;

  render() {
    return html`<div class="stack"><estimate-list></estimate-list></div>`;
  }
}
customElements.define('page-estimates', PageEstimates);

/**
 * Data Hygiene (Phase E2). The page is the card; the card owns its own request, because nothing
 * else on the screen reads that payload.
 */
class PageEstimateHygiene extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .stack {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 18px;
    }
  `;

  render() {
    return html`<div class="stack"><estimate-hygiene></estimate-hygiene></div>`;
  }
}
customElements.define('page-estimate-hygiene', PageEstimateHygiene);

/**
 * Estimate settings (Phase E4). Admin-only to change, readable by anyone who can reach the
 * tool — a planner who wonders why the ceiling is 50 should be able to see that it is 50.
 */
class PageEstimateSettings extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .stack {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 18px;
    }
  `;

  render() {
    // The schedule templates are reached from here (Estimate Tool 8.10): the compact list below
    // the settings card says what each rung publishes and opens the designer.
    return html`<div class="stack">
      <estimate-settings></estimate-settings>
      <estimate-rate-sheets></estimate-rate-sheets>
      <estimate-template-list compact></estimate-template-list>
    </div>`;
  }
}
customElements.define('page-estimate-settings', PageEstimateSettings);

/** The six rungs and what each publishes (Phase E14) — `/estimate/settings/templates`. */
class PageEstimateTemplates extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .stack {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 18px;
    }
  `;

  render() {
    return html`<div class="stack"><estimate-template-list></estimate-template-list></div>`;
  }
}
customElements.define('page-estimate-templates', PageEstimateTemplates);

/** One rung's template, and its draft (Phase E14) — `/estimate/settings/templates/:tier`. */
class PageEstimateTemplateDesigner extends LitElement {
  static properties = { route: { type: Object } };

  static styles = css`
    :host {
      display: block;
    }
  `;

  render() {
    return html`<estimate-template-designer .route=${this.route}></estimate-template-designer>`;
  }
}
customElements.define('page-estimate-template-designer', PageEstimateTemplateDesigner);

/**
 * The estimate editor (Phase E7). `/estimate/new` and `/estimate/:id` reach the same component;
 * the only difference is whether it was handed a record, which is why the route goes through.
 */
class PageEstimateEditor extends LitElement {
  static properties = { route: { type: Object } };

  static styles = css`
    :host {
      display: block;
    }
  `;

  render() {
    return html`<estimate-editor .route=${this.route}></estimate-editor>`;
  }
}
customElements.define('page-estimate-editor', PageEstimateEditor);

/**
 * Create Schedule (Estimate Tool 8.1; Phase E10) — `/estimate/:id/schedule`.
 *
 * A route under the estimate rather than a modal on it: the outline is seventy-odd lines a
 * producer reads, argues with and comes back to, and a plan that could not be linked to could not
 * be reviewed by anyone but the person who generated it.
 */
class PageEstimateSchedule extends LitElement {
  static properties = { route: { type: Object } };

  static styles = css`
    :host {
      display: block;
    }
  `;

  render() {
    return html`<estimate-schedule .route=${this.route}></estimate-schedule>`;
  }
}
customElements.define('page-estimate-schedule', PageEstimateSchedule);

/** The HubSpot deals and what each would take (Estimate Tool 13; Phase E17) — `/estimate/deals`. */
class PageEstimateDeals extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .stack {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 18px;
    }
  `;

  render() {
    return html`<div class="stack"><estimate-deal-list></estimate-deal-list></div>`;
  }
}
customElements.define('page-estimate-deals', PageEstimateDeals);

/** One deal's estimate (Estimate Tool 13) — `/estimate/deals/:id`. */
class PageEstimateDeal extends LitElement {
  static properties = { route: { type: Object } };

  static styles = css`
    :host {
      display: block;
    }
  `;

  render() {
    return html`<estimate-deal .route=${this.route}></estimate-deal>`;
  }
}
customElements.define('page-estimate-deal', PageEstimateDeal);
